import React, { createContext, useCallback, useContext, useReducer, useEffect } from 'react';
import { io } from 'socket.io-client';
import api from '../api';
import toast from 'react-hot-toast';
import { useAuth } from './AuthContext';

const NotificationContext = createContext();

const initialState = {
  notifications: [],
  unreadCount: 0,
  loading: false,
  socket: null
};

const notificationReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload.notifications,
        unreadCount: action.payload.unreadCount,
        loading: false
      };
    case 'ADD_NOTIFICATION':
      return {
        ...state,
        notifications: [action.payload, ...state.notifications],
        unreadCount: state.unreadCount + 1
      };
    case 'MARK_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n._id === action.payload ? { ...n, isRead: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      };
    case 'MARK_ALL_READ':
      return {
        ...state,
        notifications: state.notifications.map(n => ({ ...n, isRead: true })),
        unreadCount: 0
      };
    case 'REMOVE_NOTIFICATION':
      const notification = state.notifications.find(n => n._id === action.payload);
      return {
        ...state,
        notifications: state.notifications.filter(n => n._id !== action.payload),
        unreadCount: notification && !notification.isRead 
          ? Math.max(0, state.unreadCount - 1) 
          : state.unreadCount
      };
    case 'SET_SOCKET':
      return { ...state, socket: action.payload };
    case 'DISCONNECT_SOCKET':
      if (state.socket) {
        state.socket.disconnect();
      }
      return { ...state, socket: null };
    default:
      return state;
  }
};

export const NotificationProvider = ({ children }) => {
  const [state, dispatch] = useReducer(notificationReducer, initialState);
  const { user, isAuthenticated, token } = useAuth();

  const fetchNotifications = useCallback(async (page = 1, limit = 20) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const response = await api.get(`/api/notifications?page=${page}&limit=${limit}`);
      dispatch({
        type: 'SET_NOTIFICATIONS',
        payload: {
          notifications: response.data.notifications,
          unreadCount: response.data.unreadCount
        }
      });
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, []);

  // Initialize socket connection
  useEffect(() => {
    if (isAuthenticated && user && token) {
      const socket = io(process.env.REACT_APP_SOCKET_URL || process.env.REACT_APP_API_URL || 'http://localhost:5000', {
        auth: { token }
      });

      socket.on('connect', () => {
        console.log('Connected to notification server');
        socket.emit('join-room', user.id);
      });

      socket.on('notification', (notification) => {
        dispatch({ type: 'ADD_NOTIFICATION', payload: notification });
        
        // Show toast notification
        toast.success(notification.title, {
          duration: 5000,
          icon: '🔔'
        });
      });

      socket.on('disconnect', () => {
        console.log('Disconnected from notification server');
      });

      dispatch({ type: 'SET_SOCKET', payload: socket });

      return () => {
        socket.disconnect();
        dispatch({ type: 'DISCONNECT_SOCKET' });
      };
    }
  }, [isAuthenticated, user, token]);

  // Fetch notifications on mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
    }
  }, [fetchNotifications, isAuthenticated]);

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/api/notifications/${notificationId}/read`);
      dispatch({ type: 'MARK_AS_READ', payload: notificationId });
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/api/notifications/read-all');
      dispatch({ type: 'MARK_ALL_READ' });
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error);
      toast.error('Failed to mark all notifications as read');
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await api.delete(`/api/notifications/${notificationId}`);
      dispatch({ type: 'REMOVE_NOTIFICATION', payload: notificationId });
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Failed to delete notification:', error);
      toast.error('Failed to delete notification');
    }
  };

  const value = {
    ...state,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};
